import isFunction from 'lodash-es/isFunction';
import { type ForwardedRef, forwardRef, useMemo } from 'react';

import { JobHistoryLevel } from '@/common/graphql';
import { JobHistoryLevelLabels } from '@/common/types';
import { ObjectUtils } from '@/common/utils';

import type { ComboboxItemWithFormatted } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

interface JobLevelSelectSingleProps
  extends Omit<SelectSingleProps<ComboboxItemWithFormatted<JobHistoryLevel>>, 'searchable' | 'value'> {
  value: JobHistoryLevel | null;
}

interface JobLevelSelectMultipleProps
  extends Omit<SelectMultipleProps<ComboboxItemWithFormatted<JobHistoryLevel>>, 'searchable' | 'value'> {
  value: Array<JobHistoryLevel> | null | undefined;
}

const sortOrder = [
  JobHistoryLevel.L0,
  JobHistoryLevel.L1,
  JobHistoryLevel.L2,
  JobHistoryLevel.L3,
  JobHistoryLevel.L4,
  JobHistoryLevel.L5,
  JobHistoryLevel.L6,
  JobHistoryLevel.L7,
  JobHistoryLevel.CClass,
  JobHistoryLevel.Ic3,
  JobHistoryLevel.Ic4,
  JobHistoryLevel.Ic5,
  JobHistoryLevel.Ic6,
  JobHistoryLevel.Ic7,
];

/**
 * A level's seniority rank for sorting, lowest to highest. Returns `-1` (via
 * `Array.prototype.indexOf`'s not-found sentinel) for a level not present in `sortOrder` - callers
 * that sort ascending/descending by rank rely on this to push such a level to one end.
 */
export function getJobHistoryLevelRank(level: JobHistoryLevel): number {
  return sortOrder.indexOf(level);
}

const data: Array<ComboboxItemWithFormatted<JobHistoryLevel>> = ObjectUtils.arrayFromEnumValues<JobHistoryLevel>(
  JobHistoryLevel,
)
  .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
  .map(value => ({
    label: JobHistoryLevelLabels[value],
    value,
  }));

export const JobLevelSelectSingle = forwardRef(
  (
    { filter, placeholder = 'Select Level', ...props }: JobLevelSelectSingleProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const filtered = useMemo(() => {
      if (isFunction(filter)) return data.filter(filter);
      else return data;
    }, [filter]);

    return (
      <SelectSingle ref={ref} {...props} data={filtered} loading={false} placeholder={placeholder} searchable={false} />
    );
  },
);

export const JobLevelSelectMultiple = ({ placeholder = 'Select Level', ...props }: JobLevelSelectMultipleProps) => (
  <SelectMultiple
    {...props}
    data={data.filter(props.filter ?? (() => true))}
    loading={false}
    multipleSelectedLabel={length => `${length} levels`}
    placeholder={placeholder}
    searchable={false}
  />
);
